import { useMemo } from "react";
import { FlaskConical, TrendingUp, DollarSign, Lightbulb } from "lucide-react";
import { useAppData } from "@/contexts/AppDataContext";
import { productApi } from "@/resources/product";
import { useGetBrandSignals } from "@/resources/brand-signals";
import { countNonRefutedSignals } from "@/utils/brandSignalsFromAbTests";

interface BrandSignalsKPIsProps {
  withIcons?: boolean;
}

export function BrandSignalsKPIs({ withIcons = false }: BrandSignalsKPIsProps) {
  const { selectedBrandId } = useAppData();
  const { data: heroAsins = [] } = productApi.useGetBrandHeroAbTests(selectedBrandId);
  const { data: brandSignals = [] } = useGetBrandSignals(selectedBrandId);

  const stats = useMemo(() => {
    const all = heroAsins.flatMap((a) => a.tests ?? []);
    const total = all.length;
    const running = all.filter((t) => t.status === "running").length;
    const completed = all.filter((t) => t.outcome);
    const cvrDiffs = completed
      .filter((t) => t.outcome !== "inconclusive" && t.amazonMetrics?.conversion?.diff)
      .map((t) => parseFloat(t.amazonMetrics!.conversion!.diff.replace("%", "")));
    const totalCvrLift =
      cvrDiffs.length > 0
        ? Math.round(cvrDiffs.reduce((a, b) => a + Math.abs(b), 0) * 10) / 10
        : 0;
    const salesDiffs = completed
      .filter((t) => t.outcome !== "inconclusive" && t.amazonMetrics?.sales?.diff)
      .map((t) => {
        const raw = t.amazonMetrics!.sales!.diff.replace(/[^0-9.-]/g, "");
        return Math.abs(parseFloat(raw) || 0);
      });
    const totalSalesLift = salesDiffs.reduce((a, b) => a + b, 0);
    const activeSignals = countNonRefutedSignals(brandSignals);
    return { total, running, totalCvrLift, totalSalesLift, activeSignals };
  }, [heroAsins, brandSignals]);

  const items = [
    { label: "Active Signals", value: stats.activeSignals, icon: Lightbulb },
    { label: "Tests Running", value: `${stats.running} of ${stats.total}`, icon: FlaskConical },
    { label: "Total CVR Lift", value: `+${stats.totalCvrLift}%`, icon: TrendingUp },
    { label: "Incremental Sales", value: `+$${stats.totalSalesLift.toLocaleString()}`, icon: DollarSign },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map((stat) => (
        <div
          key={stat.label}
          className={`rounded-lg border border-border p-4 flex items-center gap-3${!withIcons ? " text-center justify-center" : ""}`}
        >
          {withIcons && (
            <div className="h-10 w-10 rounded-lg bg-muted/50 flex items-center justify-center shrink-0">
              <stat.icon className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
          <div className={!withIcons ? "space-y-1" : undefined}>
            <p className="text-2xl font-semibold text-foreground">{stat.value}</p>
            <p className="text-xs text-muted-foreground">{stat.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
